import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Container,
  useToast,
  Image,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
// import Header from "../components/Header";
import { pxToRem } from "../utils/editor.ts";
import { PaymentService } from "../services/paymentService";
import { CurrentPlanResponse } from "../types/Payment";
import { capitalizeFirstLetter } from "../utils/const.ts";
const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [currentPlan, setCurrentPlan] = useState<CurrentPlanResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 获取当前计划信息
  const fetchCurrentPlan = async () => {
    try {
      setIsLoading(true);
      const planData = await PaymentService.getCurrentPlan();
      setCurrentPlan(planData);
    } catch (error) {
      console.error('获取当前计划失败:', error);
      const errorMsg = error?.response?.data?.detail;
      toast({
        title: "Error",
        description: errorMsg || "Failed to fetch current plan",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 页面加载时获取数据
  useEffect(() => {
    fetchCurrentPlan();
  }, []);

  const handleHomePage = () => {
    navigate("/edit");
  };

  const handleClose = () => {
    navigate("/payment");
  };

  return (
    <Box
      h="100%"
      padding={pxToRem(30)}
      pt={0}
      overflowY={"auto"}
      display="flex"
      flexDirection="column"
      position="relative"
    >

      <Image
        w="100%"
        h="100%"
        left={'0'}
        top={'0'}
        position="fixed"
        src="/static/payment/payment-bg.png"
        objectFit="cover"
        objectPosition="center"
      />
      {/* Decorative background elements */}
      <Box
        position="absolute"
        top="20%"
        left="10%"
        w={pxToRem(200)}
        h={pxToRem(200)}
        bg="rgba(29, 138, 255, 0.1)"
        borderRadius="full"
        filter={`blur(${pxToRem(60)})`}
        zIndex={0}
      />
      <Box
        position="absolute"
        bottom="20%"
        right="10%"
        w={pxToRem(300)}
        h={pxToRem(300)}
        bg="rgba(29, 138, 255, 0.08)"
        borderRadius="full"
        filter={`blur(${pxToRem(80)})`}
        zIndex={0}
      />

      {/* Header with user menu */}
      {/* <Header /> */}

      {/* Main content */}
      <Flex flex="1" align="center" justify="center" zIndex={1}>
        <Container maxW={pxToRem(1052)}>
          {/* Payment Success Card */}
          <Box
            borderRadius={pxToRem(20)}
            pt={pxToRem(20)}
            pb={pxToRem(28)}
            paddingX={pxToRem(30)}
            boxShadow={`0 ${pxToRem(10)} ${pxToRem(40)} rgba(0, 0, 0, 0.1)`}
            maxW={pxToRem(998)}
            mx="auto"
            border="1px solid #D3D7E2"
            bg="#F5F6FA"
          >
            <Box>
              {/* Payment Successful Title */}
              <Heading
                as="h2"
                fontSize={pxToRem(32)}
                fontWeight="bold"
                color="gray.800"
              >
                Payment Successful
              </Heading>

              {/* Description */}
              <Text m={0} fontSize={pxToRem(20)} color="gray.600" lineHeight="1.5">
                Your professional plan purchase was successful, now enjoy your
                Doenba Edit professional plan features!
              </Text>
              <Box
                w={pxToRem(138)}
                h={pxToRem(138)}
                bg="#22C55E"
                borderRadius="full"
                display="flex"
                alignItems="center"
                margin={`${pxToRem(30)} auto`}
                justifyContent="center"
                boxShadow={`0 ${pxToRem(4)} ${pxToRem(
                  20
                )} rgba(34, 197, 94, 0.3)`}
              >
                <CheckIcon color="white" fontSize={pxToRem(55)} />
              </Box>

              {/* Plan Details */}
              <VStack
                w="full"
                p={pxToRem(20)}
                borderRadius={pxToRem(12)}
              >
                <HStack
                  w="full"
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Text
                    marginBottom={0}
                    fontSize={pxToRem(24)}
                    fontWeight="bold"
                    color="#333"
                    m={0} 
                  >
                    Professional Plan
                  </Text>
                  <Box
                    bg="rgba(96, 214, 151, 0.26)"
                    color="#333"
                    fontSize={pxToRem(16)}
                    px={pxToRem(20)}
                    marginLeft={pxToRem(28)}
                    py={pxToRem(5)}
                    border={`${pxToRem(1)} solid #16B560;`}
                    borderRadius={pxToRem(20)}
                    fontWeight="700"
                  >
                    Active
                  </Box>
                </HStack>

                <HStack w="full" justifyContent={"center"} mt={pxToRem(26)}>
                  <HStack marginRight={pxToRem(200)}>
                    <Text
                      fontSize={pxToRem(16)}
                      color="#8E9ABC"
                      marginRight={pxToRem(28)}
                    >
                      Billing Cycle
                    </Text>
                    <Text fontSize={pxToRem(16)} color="#333" fontWeight="700">
                      {isLoading ? 'Loading...' : (capitalizeFirstLetter(currentPlan?.plan_type) || '')}
                    </Text>
                  </HStack>

                  <HStack>
                    <Text
                      fontSize={pxToRem(16)}
                      color="#8E9ABC"
                      marginRight={pxToRem(28)}
                    >
                      Payment Method
                    </Text>
                    <Text fontSize={pxToRem(16)} color="#333" fontWeight="700">
                      {isLoading ? 'Loading...' : (capitalizeFirstLetter(currentPlan?.payment_platform) || '')}
                    </Text>
                  </HStack>
                </HStack>
              </VStack>

              {/* Action Buttons */}
              <HStack
                spacing={pxToRem(12)}
                w="full"
                pt={pxToRem(16)}
                justifyContent={"center"}
              >
                <Button
                  bg="#1D8AFF"
                  color="#1F1F1F"
                  fontSize={pxToRem(16)}
                  borderRadius={pxToRem(20)}
                  onClick={handleHomePage}
                  _hover={{ bg: "#1570CD" }}
                  fontWeight="700"
                  h={pxToRem(48)}
                  width={pxToRem(133)}
                  marginRight={pxToRem(200)}
                >
                  Home Page
                </Button>
                <Button
                  fontWeight="700"
                  h={pxToRem(48)}
                  width={pxToRem(133)}
                  fontSize={pxToRem(16)}
                  variant="outline"
                  borderColor="#1D8AFF"
                  color="gray.700"
                  size="lg"
                  borderRadius={pxToRem(20)}
                  onClick={handleClose}
                  _hover={{ bg: "gray.50" }}
                >
                  Close
                </Button>
              </HStack>
            </Box>
          </Box>
        </Container>
      </Flex>
    </Box>
  );
};

export default PaymentSuccessPage;
